
import React, { useEffect, useState } from "react";
import { getMapOrderHistory, historySteps, MapOrderHistoryRow } from "./order-map-history";
import "./order-map-popup.css";

export interface OrderLocation {
  isAssigned?: boolean;
  assignedTo?: string | null;
  assignedDate?: string | null;
  assessmentDate?: string | null;
  lat: number;
  lng: number;
  orderId: string;
  customerName: string;
  amount: number;
  createdAt?: Date | string | null;
  primaryContactNbr?: string | null;
  taxableAmount?: number;
  totalDistanceKm?: number;
  requestedDate?: Date | string | null;
  requestedBy: string | null;
  customerId?: number | null;
  addressId?: number | null;
  totalMissingCount?: number;
}

export interface InfoPopupProps {
  group: OrderLocation[];
  selectedId: string;
  onSelect: (id: string) => void;
  onClose?: () => void; // ✅ add close handler
  routeTargets?: { value: string; label: string }[];
  includedIds?: string[];
  routeActionDisabled?: boolean;
  onAddToRoute?: (orderId: string, target: string) => Promise<void>;
  onSetEndpoint?: (orderId: string, end: "start" | "end") => void | Promise<void>;
  onReverseRoute?: () => void | Promise<void>;
  onRemoveFromRoute?: (orderId: string) => void | Promise<void>;
  removeDisabled?: boolean;
}

/* ----- formatting helpers (local to popup) ----- */
function formatINR(n?: number | null): string {
  if (n == null || Number.isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `₹${(+n).toFixed(2)}`;
  }
}
function orderAmount(o: OrderLocation): number | null {
  const a = o.taxableAmount ?? o.amount;
  return typeof a === "number" ? a : null;
}

export default function InfoPopup({
  group,
  selectedId,
  onSelect,
  onClose,
  routeTargets = [], includedIds = [], routeActionDisabled = false, onAddToRoute, onSetEndpoint, onReverseRoute, onRemoveFromRoute, removeDisabled = false,
}: InfoPopupProps) {
  const selected =
    group.find((g) => String(g.orderId) === String(selectedId)) ?? group[0];
  const selectedOrderId = selected?.orderId;
  const [tab, setTab] = useState<"details" | "history">("details");
  const [history, setHistory] = useState<MapOrderHistoryRow[]>([]);
  const [historyOrderId, setHistoryOrderId] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false), [historyError, setHistoryError] = useState(false);
  const [retry, setRetry] = useState(0), [adding, setAdding] = useState(false), [addError, setAddError] = useState("");
  const [target, setTarget] = useState("");
  useEffect(() => { setTarget(routeTargets.length === 1 ? routeTargets[0].value : ""); }, [routeTargets]);
  useEffect(() => { setAddError(""); }, [selected?.orderId]);
  useEffect(() => {
    if (!selectedOrderId) return;
    let cancelled = false;
    setHistory([]); setHistoryLoading(true); setHistoryError(false);
    void getMapOrderHistory(String(selectedOrderId)).then(rows => { if (!cancelled) { setHistory(rows); setHistoryOrderId(selectedOrderId); } })
      .catch(() => { if (!cancelled) { setHistoryError(true); setHistoryOrderId(selectedOrderId); } })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrderId, retry]);
  if (!selected) return null;
  const included = includedIds.includes(String(selected.orderId));
  const loadingHistory = historyLoading || historyOrderId !== selectedOrderId;
  const runRouteAction = async (action: () => void | Promise<void>) => {
    if (adding) return;
    setAdding(true); setAddError("");
    try { await action(); } catch { setAddError("Could not update this route. Please retry."); } finally { setAdding(false); }
  };
  return <div className="smart-map-order-popup">
    <div className="map-popup-heading"><strong>Order Details · #{selected.orderId}</strong>
      {onClose && <button type="button" onClick={onClose} aria-label="Close">×</button>}</div>
    {group.length > 1 && <div className="map-popup-group" aria-label="Orders at this location">
      {group.map(order => <button type="button" key={order.orderId} aria-pressed={String(order.orderId) === String(selected.orderId)}
        onClick={() => onSelect(String(order.orderId))}>#{order.orderId}</button>)}
    </div>}
    <div className="map-popup-tabs" role="tablist" aria-label="Order information">
      {(["details", "history"] as const).map(value => <button type="button" key={value} role="tab" aria-selected={tab === value}
        onClick={() => setTab(value)}>{value === "details" ? "Details" : "Delivery history"}</button>)}
    </div>
    <div className="map-popup-body" role="tabpanel" aria-label={tab === "details" ? "Order details" : "Delivery history"}>
      {tab === "details" ? <dl><dt>Customer</dt><dd>{selected.customerName || "—"}</dd>
        <dt>Phone</dt><dd>{selected.primaryContactNbr || "—"}</dd><dt>Order value</dt><dd>{formatINR(orderAmount(selected))}</dd>
        <dt>Attempts</dt><dd>{loadingHistory ? "Loading…" : historyError ? <button type="button" onClick={() => setRetry(n => n + 1)}>Retry attempt count</button>
          : `${history.filter(row => row.assignedTo || row.assignedDate).length} recorded`}</dd></dl>
        : loadingHistory ? <p role="status"><i className="pi pi-spinner pi-spin" /> Loading history…</p>
        : historyError ? <p role="alert">Unable to load history. <button type="button" onClick={() => setRetry(n => n + 1)}>Retry history</button></p>
        : !history.length ? <p>No delivery history available.</p>
        : <ol className="map-popup-timeline" aria-label="Delivery timeline">{historySteps(history).map((step, index) =>
          <li key={index} className={step.active ? "active" : ""}><i className={`pi ${step.icon}`} aria-hidden="true" />
            <strong>{step.title}</strong>{step.detail && <span>{step.detail}</span>}
            <small>{step.date ? new Date(step.date).toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }) : "—"}</small>
          </li>)}</ol>}
    </div>
    {onSetEndpoint && <div className="map-popup-endpoint-actions">
      <button type="button" disabled={adding || routeActionDisabled} onClick={() => void runRouteAction(() => onSetEndpoint(String(selected.orderId), "start"))}>Set as start</button>
      <button type="button" disabled={adding || routeActionDisabled} onClick={() => void runRouteAction(() => onSetEndpoint(String(selected.orderId), "end"))}>Set as end</button>
      {onReverseRoute && <button type="button" disabled={adding || routeActionDisabled} onClick={() => void runRouteAction(onReverseRoute)}>Reverse route</button>}
      {onRemoveFromRoute && <button type="button" className="map-popup-remove" disabled={adding || routeActionDisabled || removeDisabled}
        title={removeDisabled ? "Keep at least one order in the route" : "Remove this order from the route"}
        onClick={() => void runRouteAction(() => onRemoveFromRoute(String(selected.orderId)))}>Remove from route</button>}
      {addError && <small role="alert">{addError}</small>}
    </div>}
    {onAddToRoute && <div className="map-popup-route-action">
      {routeTargets.length > 1 && <select aria-label="Add to which route" value={target} onChange={e => setTarget(e.target.value)} disabled={adding}>
        <option value="">Choose route</option>{routeTargets.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
      </select>}
      <button type="button" disabled={adding || included || selected.isAssigned || routeActionDisabled || !target}
        onClick={async () => { if (adding) return; setAdding(true); setAddError("");
          try { await onAddToRoute(String(selected.orderId), target); }
          catch { setAddError("Could not add this order. Please retry."); } finally { setAdding(false); }
        }}>{adding ? "Adding…" : included ? "Already in route" : "Add to route"}</button>
      {!routeTargets.length && <small>Draw a polygon first.</small>}{selected.isAssigned && <small>This order is already assigned.</small>}
      {addError && <small role="alert">{addError}</small>}
    </div>}
  </div>;
}
